import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { stockApi } from '../../api/stockApi';

const errorPayload = (error, fallback) => error.response?.data || fallback;
const itemFromPayload = (payload) => payload?.stock ?? payload;
const listFromPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  return payload?.stocks ?? payload?.results ?? [];
};

export const fetchStocks = createAsyncThunk(
  'stocks/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await stockApi.getList();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to load stock inventory'));
    }
  }
);

export const fetchStockDetail = createAsyncThunk(
  'stocks/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const response = await stockApi.getDetail(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to load stock details'));
    }
  }
);

export const createStock = createAsyncThunk(
  'stocks/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await stockApi.create(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to add stock'));
    }
  }
);

export const updateStock = createAsyncThunk(
  'stocks/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await stockApi.update(id, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to update stock'));
    }
  }
);

export const fetchCarCatalog = createAsyncThunk(
  'stocks/fetchCarCatalog',
  async (_, { rejectWithValue }) => {
    try {
      const response = await stockApi.getCarCatalog();
      return response.data;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to load car catalog'));
    }
  }
);

export const deleteStock = createAsyncThunk(
  'stocks/delete',
  async ({ id, password }, { rejectWithValue }) => {
    try {
      await stockApi.remove(id, password);
      return id;
    } catch (error) {
      return rejectWithValue(errorPayload(error, 'Failed to delete stock'));
    }
  }
);

const stockSlice = createSlice({
  name: 'stocks',
  initialState: {
    list: [],
    count: 0,
    loading: false,
    submitting: false,
    error: null,
    detail: null,
    detailLoading: false,
    detailError: null,
    catalog: [],
    catalogLoading: false,
    catalogError: null,
  },
  reducers: {
    clearStockError: (state) => { state.error = null; },
    clearStockDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStocks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStocks.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.list = listFromPayload(payload);
        state.count = payload?.count ?? payload?.total ?? state.list.length;
      })
      .addCase(fetchStocks.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      })
      .addCase(fetchStockDetail.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
        state.detail = null;
      })
      .addCase(fetchStockDetail.fulfilled, (state, { payload }) => {
        state.detailLoading = false;
        state.detail = itemFromPayload(payload);
      })
      .addCase(fetchStockDetail.rejected, (state, { payload }) => {
        state.detailLoading = false;
        state.detailError = payload;
      })
      .addCase(createStock.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createStock.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(createStock.rejected, (state, { payload }) => {
        state.submitting = false;
        state.error = payload;
      })
      .addCase(updateStock.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateStock.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(updateStock.rejected, (state, { payload }) => {
        state.submitting = false;
        state.error = payload;
      })
      .addCase(fetchCarCatalog.pending, (state) => {
        state.catalogLoading = true;
        state.catalogError = null;
      })
      .addCase(fetchCarCatalog.fulfilled, (state, { payload }) => {
        state.catalogLoading = false;
        state.catalog = Array.isArray(payload) ? payload : payload?.results ?? [];
      })
      .addCase(fetchCarCatalog.rejected, (state, { payload }) => {
        state.catalogLoading = false;
        state.catalogError = payload;
      })
      .addCase(deleteStock.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(deleteStock.fulfilled, (state, { payload: id }) => {
        state.submitting = false;
        state.list = state.list.filter((stock) => stock.id !== id);
        state.count = Math.max(0, state.count - 1);
      })
      .addCase(deleteStock.rejected, (state, { payload }) => {
        state.submitting = false;
        state.error = payload;
      });
  },
});

export const { clearStockError, clearStockDetail } = stockSlice.actions;
export default stockSlice.reducer;
